// Transactional Email Service for automated emails
import { 
  outlookCompatibleWelcomeEmail,
  outlookCompatibleOrderConfirmationEmail,
  outlookCompatibleOrderReceivedEmail,
  outlookCompatiblePasswordResetEmail,
  outlookCompatibleOrderStatusEmail,
  outlookCompatiblePasswordChangeEmail,
  outlookCompatiblePaymentSuccessEmail,
  outlookCompatiblePaymentFailedEmail,
  outlookCompatibleShippingEmail
} from './outlookCompatibleEmailTemplates';

interface OrderData {
  id: string;
  order_number?: string; // Add order_number field
  customer_name: string;
  customer_email: string;
  total_amount: number;
  status: string;
  currency?: string; // Currency of the order (INR, USD, EUR)
  items?: Array<{
    name: string;
    quantity: number;
    price: string;
    size?: string;
    color?: string;
    image?: string;
  }>;
  tracking_number?: string;
  logistics_partner?: {
    name: string;
    tracking_url_template?: string;
  };
  cod_amount?: number; // Amount to be collected on delivery
  payment_split?: boolean; // Whether order used split payment
  online_amount?: number; // Amount paid online
}

interface CustomerData {
  first_name: string;
  last_name: string;
  email: string;
  temp_password?: string; // Add temp_password field for checkout customers
}

export class TransactionalEmailService {
  private baseUrl: string;

  constructor() {
    const envBaseUrl = (import.meta as any)?.env?.VITE_EMAIL_FUNCTION_BASE as string | undefined;
    if (envBaseUrl) {
      this.baseUrl = envBaseUrl.replace(/\/?$/, '');
    } else {
      // Default to Cloudflare Pages function mounted at /send-email
      this.baseUrl = '';
    }
  }

  // Get production website URL - use custom domain to match sending domain
  private getWebsiteUrl(): string {
    // Always use the custom domain for consistency with email sending domain
    // This prevents spam filter issues from domain mismatch
    const customDomain = 'https://nirchal.com';
    
    // In development, use localhost
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      return window.location.origin;
    }
    
    // In production, always use custom domain
    return customDomain;
  }

  // Send welcome email when user signs up
  async sendWelcomeEmail(customer: CustomerData): Promise<boolean> {
    try {

      
      const html = outlookCompatibleWelcomeEmail(
        `${customer.first_name} ${customer.last_name}`,
        this.getWebsiteUrl(),
        customer.temp_password // Pass temp password if available
      );

      const subjectSuffix = customer.temp_password ? ' - Login Details Included' : '';
      const emailPayload = {
        to: customer.email,
        from: 'Nirchal <support@nirchal.com>',
        fromName: 'Nirchal',
        subject: `🎉 Welcome to Nirchal, ${customer.first_name}!${subjectSuffix}`,
        html
      };
      

      
  const response = await fetch(`${this.baseUrl}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailPayload)
      });


      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('TransactionalEmailService: Welcome email failed with response:', errorText);
      }

      return response.ok;
    } catch (error) {
      console.error('TransactionalEmailService: Failed to send welcome email:', error);
      return false;
    }
  }

  // Send password reset email
  async sendPasswordResetEmail(customer: CustomerData, resetLink: string): Promise<boolean> {
    try {
      const html = outlookCompatiblePasswordResetEmail(
        `${customer.first_name} ${customer.last_name}`,
        resetLink,
        this.getWebsiteUrl()
      );

  const response = await fetch(`${this.baseUrl}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: customer.email,
          from: 'Nirchal <support@nirchal.com>',
          fromName: 'Nirchal',
          subject: '🔒 Password Reset Request - Nirchal',
          html
        })
      });

      return response.ok;
    } catch (error) {
      console.error('Failed to send password reset email:', error);
      return false;
    }
  }

  // Send password change confirmation email
  async sendPasswordChangeEmail(customer: CustomerData): Promise<boolean> {
    try {
      const html = outlookCompatiblePasswordChangeEmail(
        `${customer.first_name} ${customer.last_name}`,
        this.getWebsiteUrl()
      );

  const response = await fetch(`${this.baseUrl}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: customer.email,
          from: 'Nirchal <support@nirchal.com>',
          fromName: 'Nirchal',
          subject: '✅ Password Updated Successfully - Nirchal',
          html
        })
      });

      return response.ok;
    } catch (error) {
      console.error('Failed to send password change confirmation:', error);
      return false;
    }
  }

  // Send order confirmation email
  async sendOrderConfirmationEmail(order: OrderData): Promise<boolean> {
    try {

      
      const orderNumber = order.order_number || `ORD${order.id}`;
      const html = outlookCompatibleOrderConfirmationEmail(
        order.customer_name,
        orderNumber,
        (order.total_amount || 0).toString(),
        this.getWebsiteUrl(),
        order.items,
        order.cod_amount,
        order.payment_split,
        order.online_amount,
        order.currency
      );

      const emailPayload = {
        to: order.customer_email,
        from: 'Nirchal <support@nirchal.com>',
        fromName: 'Nirchal',
        subject: `✅ Order Confirmed ${orderNumber} - Nirchal`,
        html
      };
      


  const response = await fetch(`${this.baseUrl}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailPayload)
      });


      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('TransactionalEmailService: Order confirmation email failed with response:', errorText);
      }

      return response.ok;
    } catch (error) {
      console.error('TransactionalEmailService: Failed to send order confirmation email:', error);
      return false;
    }
  }

  // Send order received email (when order is first placed)
  async sendOrderReceivedEmail(order: OrderData): Promise<boolean> {
    try {
      const websiteUrl = this.getWebsiteUrl();
      
      const orderNumber = order.order_number || `ORD${order.id}`;
      const html = outlookCompatibleOrderReceivedEmail(
        order.customer_name,
        orderNumber,
        (order.total_amount || 0).toString(),
        websiteUrl,
        order.items,
        order.currency
      );

      const emailPayload = {
        to: order.customer_email,
        from: 'Nirchal <support@nirchal.com>',
        fromName: 'Nirchal',
        subject: `Order Received for Processing - ${orderNumber}`,
        html: html
      };
      


  const response = await fetch(`${this.baseUrl}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailPayload)
      });


      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('TransactionalEmailService: Order received email failed with response:', errorText);
      }

      return response.ok;
    } catch (error) {
      console.error('TransactionalEmailService: Failed to send order received email:', error);
      return false;
    }
  }

  // Send order status update email
  async sendOrderStatusUpdateEmail(order: OrderData): Promise<boolean> {
    try {
      const orderNumber = order.order_number || `ORD${order.id}`;
      const html = outlookCompatibleOrderStatusEmail(
        order.customer_name,
        orderNumber,
        order.status,
        order.tracking_number,
        this.getWebsiteUrl()
      );

  const response = await fetch(`${this.baseUrl}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: order.customer_email,
          from: 'Nirchal <support@nirchal.com>',
          fromName: 'Nirchal',
          subject: `📦 Order Update ${orderNumber} - ${order.status.toUpperCase()} - Nirchal`,
          html
        })
      });

      return response.ok;
    } catch (error) {
      console.error('Failed to send order status update email:', error);
      return false;
    }
  }

  // Send shipping notification email with tracking details
  async sendShippingEmail(order: OrderData): Promise<boolean> {
    try {

      
      if (!order.tracking_number) {
        console.error('TransactionalEmailService: Cannot send shipping email - no tracking number provided');
        return false;
      }

      const orderNumber = order.order_number || `ORD${order.id}`;
      const logisticsPartner = order.logistics_partner?.name || 'Logistics Partner';
      
      // Generate tracking URL if template is available
      let trackingUrl: string | undefined;
      if (order.logistics_partner?.tracking_url_template && order.tracking_number) {
        trackingUrl = order.logistics_partner.tracking_url_template.replace('{tracking_number}', order.tracking_number);
      }
      
      const html = outlookCompatibleShippingEmail(
        order.customer_name,
        orderNumber,
        order.tracking_number,
        trackingUrl,
        logisticsPartner,
        this.getWebsiteUrl()
      );

      const emailPayload = {
        to: order.customer_email,
        from: 'Nirchal <support@nirchal.com>',
        fromName: 'Nirchal',
        subject: `🚚 Your Order ${orderNumber} is Shipped! - Track: ${order.tracking_number} - Nirchal`,
        html
      };



  const response = await fetch(`${this.baseUrl}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailPayload)
      });


      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('TransactionalEmailService: Shipping email failed with response:', errorText);
      }

      return response.ok;
    } catch (error) {
      console.error('TransactionalEmailService: Failed to send shipping email:', error);
      return false;
    }
  }

  // Send payment success email
  async sendPaymentSuccessEmail(paymentData: {
    customer_name: string;
    customer_email: string;
    order_number: string;
    amount: number;
    payment_id: string;
    currency?: string;
  }): Promise<boolean> {
    try {

      
      const html = outlookCompatiblePaymentSuccessEmail(
        paymentData.customer_name,
        paymentData.order_number,
        paymentData.amount.toString(),
        paymentData.payment_id,
        this.getWebsiteUrl(),
        paymentData.currency
      );

      const emailPayload = {
        to: paymentData.customer_email,
        from: 'Nirchal <support@nirchal.com>',
        fromName: 'Nirchal',
        subject: `✅ Payment Successful - Order ${paymentData.order_number} - Nirchal`,
        html
      };
      


  const response = await fetch(`${this.baseUrl}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailPayload)
      });


      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('TransactionalEmailService: Payment success email failed with response:', errorText);
      }

      return response.ok;
    } catch (error) {
      console.error('TransactionalEmailService: Failed to send payment success email:', error);
      return false;
    }
  }

  // Send payment failure email
  async sendPaymentFailureEmail(paymentData: {
    customer_name: string;
    customer_email: string;
    order_number: string;
    amount: number;
    error_reason: string;
    currency?: string;
    recovery_link?: string;
  }): Promise<boolean> {
    try {

      
      const html = outlookCompatiblePaymentFailedEmail(
        paymentData.customer_name,
        paymentData.order_number,
        paymentData.amount.toString(),
        paymentData.error_reason,
        this.getWebsiteUrl(),
        paymentData.currency,
        paymentData.recovery_link
      );

      const emailPayload = {
        to: paymentData.customer_email,
        from: 'Nirchal <support@nirchal.com>',
        fromName: 'Nirchal',
        subject: `❌ Payment Failed - Order ${paymentData.order_number} - Nirchal`,
        html
      };
      


  const response = await fetch(`${this.baseUrl}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailPayload)
      });


      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('TransactionalEmailService: Payment failure email failed with response:', errorText);
      }

      // Send admin notification for payment failure
      try {
        const getCurrencySymbol = (currency?: string): string => {
          const currencyMap: { [key: string]: string } = { 'INR': '₹', 'USD': '$', 'EUR': '€' };
          return currencyMap[currency || 'INR'] || '₹';
        };
        const currencySymbol = getCurrencySymbol(paymentData.currency);
        const adminHtml = `
          <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 600px; margin: 0 auto; background-color: #ffffff;">
            <div style="background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%); padding: 30px; text-align: center;">
              <h1 style="color: white; margin: 0; font-size: 24px; font-weight: 600;">⚠️ Payment Failed Alert</h1>
            </div>
            
            <div style="padding: 30px; background-color: #ffffff;">
              <div style="background-color: #fef2f2; border: 1px solid #fecaca; border-radius: 12px; padding: 25px; margin-bottom: 25px;">
                <h2 style="color: #991b1b; margin: 0 0 20px 0; font-size: 20px; font-weight: 600;">Payment Failure Details</h2>
                
                <table style="width: 100%; border-collapse: collapse;">
                  <tr>
                    <td style="padding: 8px 0; color: #6b7280; font-weight: 500;">Order Number:</td>
                    <td style="padding: 8px 0; color: #1f2937; font-weight: 600;">${paymentData.order_number}</td>
                  </tr>
                  <tr>
                    <td style="padding: 8px 0; color: #6b7280; font-weight: 500;">Customer Name:</td>
                    <td style="padding: 8px 0; color: #1f2937; font-weight: 600;">${paymentData.customer_name}</td>
                  </tr>
                  <tr>
                    <td style="padding: 8px 0; color: #6b7280; font-weight: 500;">Customer Email:</td>
                    <td style="padding: 8px 0; color: #1f2937; font-weight: 600;">${paymentData.customer_email}</td>
                  </tr>
                  <tr>
                    <td style="padding: 8px 0; color: #6b7280; font-weight: 500;">Amount:</td>
                    <td style="padding: 8px 0; color: #ef4444; font-weight: 700; font-size: 18px;">${currencySymbol}${paymentData.amount.toLocaleString()}</td>
                  </tr>
                  <tr>
                    <td style="padding: 8px 0; color: #6b7280; font-weight: 500;">Failure Reason:</td>
                    <td style="padding: 8px 0; color: #ef4444; font-weight: 600;">${paymentData.error_reason}</td>
                  </tr>
                  <tr>
                    <td style="padding: 8px 0; color: #6b7280; font-weight: 500;">Time:</td>
                    <td style="padding: 8px 0; color: #1f2937; font-weight: 600;">${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST</td>
                  </tr>
                </table>
              </div>
              
              <div style="text-align: center; margin-top: 20px;">
                <p style="color: #6b7280; margin: 0; font-size: 14px;">
                  The customer has been notified and can retry payment from their account.
                </p>
              </div>
            </div>
            
            <div style="background-color: #f9fafb; padding: 20px; text-align: center; border-top: 1px solid #e5e7eb;">
              <p style="color: #6b7280; margin: 0; font-size: 12px;">
                This is an automated notification from Nirchal e-commerce system.
              </p>
            </div>
          </div>
        `;

        const adminEmailPayload = {
          to: 'amit.ranjan78@gmail.com',
          from: 'Nirchal <support@nirchal.com>',
          fromName: 'Nirchal',
          subject: `⚠️ Payment Failed - Order #${paymentData.order_number} - ${currencySymbol}${paymentData.amount.toLocaleString()} - Nirchal`,
          html: adminHtml
        };

        await fetch(`${this.baseUrl}/send-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(adminEmailPayload)
        });
      } catch (adminEmailError) {
        console.error('TransactionalEmailService: Failed to send admin payment failure notification:', adminEmailError);
      }

      return response.ok;
    } catch (error) {
      console.error('TransactionalEmailService: Failed to send payment failure email:', error);
      return false;
    }
  }

  // Send order notification to support team
  async sendOrderNotificationToSupport(orderData: {
    order_number: string;
    customer_name: string;
    customer_email: string;
    total_amount: number;
    items_count: number;
    payment_method: string;
    currency?: string;
  }): Promise<boolean> {
    try {

      
      const getCurrencySymbol = (currency?: string): string => {
        const currencyMap: { [key: string]: string } = { 'INR': '₹', 'USD': '$', 'EUR': '€' };
        return currencyMap[currency || 'INR'] || '₹';
      };
      const currencySymbol = getCurrencySymbol(orderData.currency);
      const html = `
        <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 600px; margin: 0 auto; background-color: #ffffff;">
          <div style="background: linear-gradient(135deg, #f59e0b 0%, #ea580c 100%); padding: 30px; text-align: center;">
            <h1 style="color: white; margin: 0; font-size: 24px; font-weight: 600;">🎉 New Order Received!</h1>
          </div>
          
          <div style="padding: 30px; background-color: #ffffff;">
            <div style="background-color: #f8fafc; border-radius: 12px; padding: 25px; margin-bottom: 25px;">
              <h2 style="color: #1f2937; margin: 0 0 20px 0; font-size: 20px; font-weight: 600;">Order Details</h2>
              
              <table style="width: 100%; border-collapse: collapse;">
                <tr>
                  <td style="padding: 8px 0; color: #6b7280; font-weight: 500;">Order Number:</td>
                  <td style="padding: 8px 0; color: #1f2937; font-weight: 600;">${orderData.order_number}</td>
                </tr>
                <tr>
                  <td style="padding: 8px 0; color: #6b7280; font-weight: 500;">Customer:</td>
                  <td style="padding: 8px 0; color: #1f2937; font-weight: 600;">${orderData.customer_name}</td>
                </tr>
                <tr>
                  <td style="padding: 8px 0; color: #6b7280; font-weight: 500;">Email:</td>
                  <td style="padding: 8px 0; color: #1f2937; font-weight: 600;">${orderData.customer_email}</td>
                </tr>
                <tr>
                  <td style="padding: 8px 0; color: #6b7280; font-weight: 500;">Total Amount:</td>
                  <td style="padding: 8px 0; color: #10b981; font-weight: 700; font-size: 18px;">${currencySymbol}${orderData.total_amount.toLocaleString()}</td>
                </tr>
                <tr>
                  <td style="padding: 8px 0; color: #6b7280; font-weight: 500;">Items Count:</td>
                  <td style="padding: 8px 0; color: #1f2937; font-weight: 600;">${orderData.items_count} item(s)</td>
                </tr>
                <tr>
                  <td style="padding: 8px 0; color: #6b7280; font-weight: 500;">Payment Method:</td>
                  <td style="padding: 8px 0; color: #1f2937; font-weight: 600;">${orderData.payment_method}</td>
                </tr>
              </table>
            </div>
            
            <div style="text-align: center; margin-top: 30px;">
              <p style="color: #6b7280; margin: 0; font-size: 14px;">
                Please process this order promptly and update the customer with shipping details.
              </p>
            </div>
          </div>
          
          <div style="background-color: #f9fafb; padding: 20px; text-align: center; border-top: 1px solid #e5e7eb;">
            <p style="color: #6b7280; margin: 0; font-size: 12px;">
              This is an automated notification from Nirchal e-commerce system.
            </p>
          </div>
        </div>
      `;

      const emailPayload = {
        to: 'amit.ranjan78@gmail.com', // Primary admin email
        from: 'Nirchal <support@nirchal.com>',
        fromName: 'Nirchal',
        subject: `🛍️ New Order #${orderData.order_number} - ${currencySymbol}${orderData.total_amount.toLocaleString()} - Nirchal`,
        html
      };
      


      const response = await fetch(`${this.baseUrl}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailPayload)
      });


      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('TransactionalEmailService: Order notification email failed with response:', errorText);
      }

      return response.ok;
    } catch (error) {
      console.error('TransactionalEmailService: Failed to send order notification email:', error);
      return false;
    }
  }

  // Send signup notification to support team
  async sendSignupNotificationToSupport(customerData: {
    customer_name: string;
    customer_email: string;
    phone?: string;
    signup_date: string;
  }): Promise<boolean> {
    try {

      
      const html = `
        <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 600px; margin: 0 auto; background-color: #ffffff;">
          <div style="background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); padding: 30px; text-align: center;">
            <h1 style="color: white; margin: 0; font-size: 24px; font-weight: 600;">🎊 New Customer Signup!</h1>
          </div>
          
          <div style="padding: 30px; background-color: #ffffff;">
            <div style="background-color: #f8fafc; border-radius: 12px; padding: 25px; margin-bottom: 25px;">
              <h2 style="color: #1f2937; margin: 0 0 20px 0; font-size: 20px; font-weight: 600;">Customer Details</h2>
              
              <table style="width: 100%; border-collapse: collapse;">
                <tr>
                  <td style="padding: 8px 0; color: #6b7280; font-weight: 500;">Name:</td>
                  <td style="padding: 8px 0; color: #1f2937; font-weight: 600;">${customerData.customer_name}</td>
                </tr>
                <tr>
                  <td style="padding: 8px 0; color: #6b7280; font-weight: 500;">Email:</td>
                  <td style="padding: 8px 0; color: #1f2937; font-weight: 600;">${customerData.customer_email}</td>
                </tr>
                ${customerData.phone ? `
                <tr>
                  <td style="padding: 8px 0; color: #6b7280; font-weight: 500;">Phone:</td>
                  <td style="padding: 8px 0; color: #1f2937; font-weight: 600;">${customerData.phone}</td>
                </tr>
                ` : ''}
                <tr>
                  <td style="padding: 8px 0; color: #6b7280; font-weight: 500;">Signup Date:</td>
                  <td style="padding: 8px 0; color: #1f2937; font-weight: 600;">${customerData.signup_date}</td>
                </tr>
              </table>
            </div>
            
            <div style="text-align: center; margin-top: 30px;">
              <p style="color: #6b7280; margin: 0; font-size: 14px;">
                A new customer has joined Nirchal! Consider sending them a welcome offer or checking if they need assistance.
              </p>
            </div>
          </div>
          
          <div style="background-color: #f9fafb; padding: 20px; text-align: center; border-top: 1px solid #e5e7eb;">
            <p style="color: #6b7280; margin: 0; font-size: 12px;">
              This is an automated notification from Nirchal e-commerce system.
            </p>
          </div>
        </div>
      `;

      const emailPayload = {
        to: 'support@nirchal.com',
        from: 'Nirchal <support@nirchal.com>',
        fromName: 'Nirchal',
        subject: `👋 New Customer Signup - ${customerData.customer_name} - Nirchal`,
        html
      };
      


      const response = await fetch(`${this.baseUrl}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailPayload)
      });


      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('TransactionalEmailService: Signup notification email failed with response:', errorText);
      }

      return response.ok;
    } catch (error) {
      console.error('TransactionalEmailService: Failed to send signup notification email:', error);
      return false;
    }
  }
}

// Export singleton instance
export const transactionalEmailService = new TransactionalEmailService();
